import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {IconArrowRight} from '@tabler/icons-react';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  InspectionStateEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {FullBleed} from 'src/design/molecules/FullBleed';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppSelector} from 'src/redux/hooks';
import {useGetInspectionQuery} from 'src/redux/queries/inspection';
import {useGetSiteQuery} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CompletionConfirmPanel} from './components/CompletionConfirmPanel';
import {GuidedFlowBar} from './components/GuidedFlowBar';
import {InspectionMyCompletedScreen} from './components/InspectionMyCompletedScreen';
import {InspectionMyExecuteScreen} from './components/InspectionMyExecuteScreen';
import {InspectionMyOverviewSummary} from './components/InspectionMyOverviewSummary';
import {InspectionOfflinePanel} from './components/InspectionOfflinePanel';
import {PrepareInspectionCard} from './components/PrepareInspectionCard';
import {StartConfirmPanel} from './components/StartConfirmPanel';
import {useInspectionOfflineSession} from 'src/app/inspections/offline/useInspectionOfflineSession';
import {useInspectionSync} from 'src/app/inspections/offline/useInspectionSync';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
import {BackButton} from 'src/app/layout/BackBar';

// `screen` only chooses between the two pre-start linear steps
// (overview/confirmLocation) — once InProgress, the state-derived branch below ignores it.
type Screen = 'overview' | 'confirmLocation';

// px — matches Box maw={760}/{640} convention elsewhere (AttachmentForm.tsx,
// InspectionForm.tsx); applies only to the linear overview/confirm screens, not the full-bleed execute screen.
const GUIDED_FLOW_MAX_WIDTH = 880;

export const InspectionMyPage: React.FC = () => {
  const {id} = useParams();
  const inspectionId = id ? Number(id) : undefined;
  const navigate = useAppNavigate();
  const [screen, setScreen] = useState<Screen>('overview');
  // Scoped to the InProgress branch only — `screen` above still governs the
  // Scheduled overview/confirm pair exclusively.
  const [completing, setCompleting] = useState(false);
  const currentUserId = useAppSelector(state => state.session.user?.id);

  const exitPath = PathUtility.Inspections('list');
  const {
    data: inspection,
    isLoading: isInspectionLoading,
    isError: isInspectionError,
    error: inspectionError,
  } = useGetInspectionQuery(inspectionId!, {skip: !inspectionId});

  // Page-level so offline state survives the execute→completed sub-view swap.
  const offlineState = useInspectionOfflineSession(inspectionId);
  const {session: offlineSession} = offlineState;
  const {sync, isSyncing} = useInspectionSync(offlineSession?.id);

  const {data: site} = useGetSiteQuery(inspection?.siteId ?? 0, {
    skip: !inspection,
  });

  // Generated client can carry an explicit `null` for unset DB columns even
  // though the TS type says `number | undefined` — normalize at the boundary.
  const siteCoords =
    site?.latitude != null && site?.longitude != null
      ? {latitude: site.latitude, longitude: site.longitude}
      : undefined;

  // Deep links from external systems can point at a deleted or now-inaccessible
  // inspection — surface the failure instead of spinning forever.
  if (isInspectionError) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <FullBleed variant="frame">
          <GuidedFlowBar exitTo={exitPath} />
        </FullBleed>
        <Box maw={GUIDED_FLOW_MAX_WIDTH} w="100%" mx="auto" mt="lg">
          <Alert color="red" title="Could not load inspection">
            {parseErrorMessage(inspectionError)}{' '}
            <BackButton
              fallbackTo={exitPath}
              fallbackLabel="Back to inspections"
            />
          </Alert>
        </Box>
      </ContentWrapper>
    );
  }

  if (!inspectionId || isInspectionLoading || !inspection) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <FullBleed variant="frame">
          <GuidedFlowBar exitTo={exitPath} />
        </FullBleed>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  const viewLink = PathUtility.InspectionsView(inspectionId);

  const isAssignee = inspection.assigneeId === currentUserId;

  // Rendered only on the completing/completed views where the inspector syncs;
  // the page-level sync hooks keep auto-sync alive on every sub-view regardless.
  const offlinePanel = (
    <InspectionOfflinePanel
      session={offlineState.session}
      isDownloaded={offlineState.isDownloaded}
      pendingCount={offlineState.pendingCount}
      failedCount={offlineState.failedCount}
      syncedCount={offlineState.syncedCount}
      outboxItems={offlineState.outboxItems}
      sync={sync}
      isSyncing={isSyncing}
    />
  );

  // State-derived screen model: Draft/Completed are precondition outcomes regardless of
  // assignee; InProgress branches on assignee (execute screen vs. bounced to admin monitor).
  let preconditionReason: string | undefined;
  if (inspection.state === InspectionStateEnum.Draft) {
    preconditionReason = 'Not yet scheduled.';
  } else if (inspection.state === InspectionStateEnum.Completed) {
    preconditionReason = isAssignee ? undefined : 'Already completed.';
  } else if (inspection.state === InspectionStateEnum.InProgress) {
    preconditionReason = isAssignee
      ? undefined
      : "This inspection isn't assigned to you. Another inspector has it in progress.";
  } else if (!isAssignee) {
    preconditionReason = "This inspection isn't assigned to you.";
  }

  if (preconditionReason) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <FullBleed variant="frame">
          <GuidedFlowBar exitTo={exitPath} siteName={inspection.siteName} />
        </FullBleed>
        <Box maw={GUIDED_FLOW_MAX_WIDTH} w="100%" mx="auto" mt="lg">
          <Alert color="red">
            {preconditionReason}{' '}
            <Button
              variant="subtle"
              color="black"
              rightSection={<IconArrowRight size={14} stroke={1.5} />}
              onClick={() => navigate(viewLink)}>
              View inspection
            </Button>
          </Alert>
        </Box>
      </ContentWrapper>
    );
  }

  if (inspection.state === InspectionStateEnum.InProgress) {
    // isAssignee guaranteed true — the precondition branch already caught
    // the non-assignee case.
    if (completing) {
      return (
        <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
          <FullBleed variant="frame">
            <GuidedFlowBar exitTo={exitPath} siteName={inspection.siteName} />
          </FullBleed>
          <Box maw={GUIDED_FLOW_MAX_WIDTH} w="100%" mx="auto" mt="lg">
            <Stack gap="lg">
              {offlinePanel}
              <CompletionConfirmPanel
                inspection={inspection}
                site={siteCoords}
                onBack={() => setCompleting(false)}
              />
            </Stack>
          </Box>
        </ContentWrapper>
      );
    }
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <FullBleed variant="full">
          <GuidedFlowBar exitTo={exitPath} siteName={inspection.siteName} />
          <InspectionMyExecuteScreen
            inspection={inspection}
            onCompleteRequested={() => setCompleting(true)}
          />
        </FullBleed>
      </ContentWrapper>
    );
  }

  if (inspection.state === InspectionStateEnum.Completed) {
    // isAssignee guaranteed true. `completing` stays `true` after a successful complete
    // transition but is never rechecked once state is Completed — this branch matches first.
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
        <FullBleed variant="frame">
          <GuidedFlowBar exitTo={exitPath} siteName={inspection.siteName} />
        </FullBleed>
        <Box maw={GUIDED_FLOW_MAX_WIDTH} w="100%" mx="auto" mt="lg">
          <Stack gap="lg">
            {offlinePanel}
            <InspectionMyCompletedScreen inspection={inspection} />
          </Stack>
        </Box>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Inspections}>
      <FullBleed variant="frame">
        <GuidedFlowBar exitTo={exitPath} siteName={inspection.siteName} />
      </FullBleed>
      <Box maw={GUIDED_FLOW_MAX_WIDTH} w="100%" mx="auto" mt="lg">
        <Stack gap="lg">
          {screen === 'confirmLocation' ? (
            <StartConfirmPanel
              inspectionId={inspectionId}
              site={siteCoords}
              onBack={() => setScreen('overview')}
            />
          ) : (
            <>
              <InspectionMyOverviewSummary inspection={inspection} site={site} />

              <PrepareInspectionCard inspectionId={inspectionId} />

              <Button
                fullWidth
                size="lg"
                color="yellow"
                rightSection={<IconArrowRight size={20} stroke={2} />}
                onClick={() => setScreen('confirmLocation')}>
                Start inspection
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.InspectionsMy(':id'),
  element: <InspectionMyPage />,
  module: ModuleEnum.Inspections,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  supportsOffline: true,
});
